import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ChevronLeft, Loader2, ListMusic, Pencil, Plus,
  Repeat, Radio, Trash2, ArrowUp, ArrowDown, Music, Check,
  Volume2, VolumeX,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Switch } from '@meno/ui/components/ui/switch'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import {
  getAudioPlaylist, updateAudioPlaylist, publishAudioPlaylist, getAudios,
} from '@meno/ui/lib/api'
import { type AudioFile, type AudioPlaylist, type AudioPlaylistTrack } from './management/media-page/utils'
import { PublishDialog } from '@/components/PublishDialog'
import PreviewPanel, { type PreviewItem } from '@/components/ContentPreview'

// Audio playlist editor — the audio-only sibling of PlaylistDetailsPage. An
// ordered list of audio tracks with per-track volume + loop and a whole-
// sequence loop toggle. "Stream" publishes the sequence to screens/groups,
// overriding any audio currently playing there. Mutations persist immediately
// (optimistic local state + a full-track-list PATCH).
export default function AudioPlaylistDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  // Always ChevronLeft — a global CSS rule mirrors it under dir="rtl"; see
  // WizardNavButton.tsx for why swapping it here too double-flips it back.
  const ChevronIcon = ChevronLeft

  const [pl, setPl] = useState<AudioPlaylist | null>(null)
  const [loading, setLoading] = useState(true)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const titleInputRef = useRef<HTMLInputElement | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  // Remembers each track's volume before it was muted so unmute can restore it.
  // Mute is modelled as volume 0 (no separate flag), so the player already
  // honours it and no backend change is needed.
  const prevVolRef = useRef<Record<number, number>>({})

  useEffect(() => {
    setLoading(true)
    getAudioPlaylist(Number(id))
      .then(r => setPl(r.data))
      .catch(() => toast.error(t('common.error', 'Something went wrong')))
      .finally(() => setLoading(false))
  }, [id, t])

  // Persist the current track set + flags. Sends the whole ordered list so the
  // backend can rewrite positions/volume/loop in one shot.
  const persist = async (next: AudioPlaylist) => {
    setPl(next)
    try {
      await updateAudioPlaylist(next.id, {
        name: next.name,
        audio_loop_all: next.audio_loop_all,
        tracks: next.tracks.map(tr => ({ audio_id: tr.id, volume: tr.volume, loop: tr.loop })),
      })
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    }
  }

  const commitTitle = async () => {
    setEditingTitle(false)
    const v = titleDraft.trim()
    if (!pl || !v || v === pl.name) return
    await persist({ ...pl, name: v })
  }

  const updateTrack = (audioId: number, patch: Partial<AudioPlaylistTrack>) => {
    if (!pl) return
    persist({ ...pl, tracks: pl.tracks.map(tr => tr.id === audioId ? { ...tr, ...patch } : tr) })
  }
  const removeTrack = (audioId: number) => {
    if (!pl) return
    persist({ ...pl, tracks: pl.tracks.filter(tr => tr.id !== audioId) })
  }
  // Toggle mute by driving volume to 0 (remembering the prior value) and back.
  const toggleMute = (tr: AudioPlaylistTrack) => {
    if (tr.volume > 0) {
      prevVolRef.current[tr.id] = tr.volume
      updateTrack(tr.id, { volume: 0 })
    } else {
      updateTrack(tr.id, { volume: prevVolRef.current[tr.id] ?? 100 })
    }
  }
  const move = (idx: number, dir: -1 | 1) => {
    if (!pl) return
    const next = [...pl.tracks]
    const j = idx + dir
    if (j < 0 || j >= next.length) return
    ;[next[idx], next[j]] = [next[j], next[idx]]
    persist({ ...pl, tracks: next.map((tr, i) => ({ ...tr, position: i })) })
  }
  const addTracks = (files: AudioFile[]) => {
    if (!pl) return
    const existing = new Set(pl.tracks.map(tr => tr.id))
    const added: AudioPlaylistTrack[] = files
      .filter(f => !existing.has(f.id))
      .map((f, i) => ({
        id: f.id, original_name: f.original_name, url: f.url, duration: f.duration,
        position: pl.tracks.length + i, volume: 100, loop: false,
      }))
    if (added.length === 0) return
    persist({ ...pl, tracks: [...pl.tracks, ...added] })
    setPickerOpen(false)
  }

  // ── Header ───────────────────────────────────────────────────────────────
  const infoBar = pl ? (
    <div className="flex items-center justify-between gap-3 w-full ps-2 pe-3">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => navigate('/audio-hub')}
          aria-label={t('common.back', 'Back')}
          className="w-9 h-9 flex items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors shrink-0"
        >
          <ChevronIcon className="w-4 h-4" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-pink-500/15 flex items-center justify-center shrink-0">
          <ListMusic className="w-5 h-5 text-pink-500" />
        </div>
        <div className="min-w-0 flex items-center gap-1.5">
          {editingTitle ? (
            <input
              ref={titleInputRef}
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={e => {
                if (e.key === 'Enter') commitTitle()
                if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(pl.name) }
              }}
              maxLength={150}
              className="text-[15px] font-semibold leading-tight bg-transparent border-b border-primary outline-none min-w-0 w-48"
            />
          ) : (
            <>
              <h1 className="text-[15px] font-semibold text-foreground leading-tight truncate">{pl.name}</h1>
              <button
                type="button"
                onClick={() => { setTitleDraft(pl.name); setEditingTitle(true); queueMicrotask(() => titleInputRef.current?.select()) }}
                aria-label={t('common.rename', 'Rename')}
                className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <Button onClick={() => setPublishOpen(true)} disabled={pl.tracks.length === 0} className="gap-2 h-9">
        <Radio className="w-4 h-4" />
        {t('audioHub.stream', 'Stream')}
      </Button>
    </div>
  ) : null
  usePageHeader(infoBar ? { render: infoBar } : { title: t('nav.audioHub', 'Audio Hub') })

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
  }
  if (!pl) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-muted-foreground">
        <ListMusic className="w-10 h-10 opacity-30" />
        <p className="text-sm font-medium">{t('audioHub.playlistNotFound', 'Audio playlist not found.')}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/audio-hub')}>{t('common.back', 'Back')}</Button>
      </div>
    )
  }

  // Tracks → the shared audio preview (a "now playing" card that plays each
  // track and advances on end). Muted tracks (volume 0) preview silently.
  const audioItems: PreviewItem[] = pl.tracks.map(tr => ({
    key: `a-${tr.id}`,
    name: tr.original_name,
    kind: 'audio',
    url: tr.url,
    durationSec: tr.duration ?? 30,
    muted: tr.volume === 0,
  }))

  return (
    <div className="-m-4 sm:-m-[var(--space-l)] p-4 sm:p-[var(--space-l)] h-[calc(100dvh-3.5rem-1px)] flex flex-col bg-background">
      {/* Two distinct cards on the canvas — the tracks/config editor and the
          preview each read as their own bordered, rounded card with a gutter
          between them (mirrors the screen page's Content | Preview split). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">

        {/* ── Left — tracks + config ── */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {/* Whole-sequence loop */}
          <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/40 px-4 py-3 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <Repeat className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('audioHub.loopAll', 'Loop whole sequence')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('audioHub.loopAllHint', 'Play each track once in order, then repeat from the top.')}
                </p>
              </div>
            </div>
            <Switch checked={pl.audio_loop_all} onCheckedChange={v => persist({ ...pl, audio_loop_all: v })} />
          </div>

          {/* Tracks header */}
          <div className="flex items-center justify-between mt-5 mb-3 shrink-0">
            <h3 className="text-sm font-semibold">
              {t('audioHub.tracks', 'Tracks')}
              <span className="ms-2 text-xs font-normal text-muted-foreground">{pl.tracks.length}</span>
            </h3>
            <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setPickerOpen(true)}>
              <Plus className="w-3.5 h-3.5" />
              {t('audioHub.addTracks', 'Add tracks')}
            </Button>
          </div>

          {/* Tracks list — scrolls within the column. */}
          <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2">
            {pl.tracks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
                {t('audioHub.noTracks', 'No tracks yet — add some from your library.')}
              </div>
            ) : (
              <ul className="space-y-2">
                {pl.tracks.map((tr, idx) => (
                  <li key={tr.id} className="group flex items-center gap-2 rounded-xl border border-border/60 bg-muted/40 px-3 py-2 h-14 transition-colors hover:bg-muted/60">
                    <span className="text-xs text-muted-foreground font-mono w-5 text-center shrink-0">{idx + 1}</span>
                    <div className="w-9 h-9 rounded-lg bg-pink-500/10 flex items-center justify-center shrink-0">
                      <Music className="w-4 h-4 text-pink-500" />
                    </div>
                    <span className="text-sm font-medium truncate flex-1 min-w-0">{tr.original_name}</span>

                    {/* Mute — modelled as volume 0, restoring the prior volume on unmute */}
                    <button
                      type="button"
                      onClick={() => toggleMute(tr)}
                      aria-label={tr.volume === 0
                        ? t('audioHub.unmute', 'Unmute track')
                        : t('audioHub.mute', 'Mute track')}
                      title={tr.volume === 0
                        ? t('audioHub.unmute', 'Unmute track')
                        : t('audioHub.mute', 'Mute track')}
                      className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors shrink-0 ${
                        tr.volume === 0
                          ? 'text-amber-600 hover:bg-amber-500/10'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {tr.volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>

                    {/* Volume — compact so the row fits the narrower column. */}
                    <div className="hidden sm:flex items-center gap-1.5 shrink-0" title={t('audioHub.volume', 'Volume')}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={tr.volume}
                        onChange={e => updateTrack(tr.id, { volume: Number(e.target.value) })}
                        className="w-16 range-fill"
                        style={{ '--range-fill': `${tr.volume}%` } as React.CSSProperties}
                      />
                      <span className="text-[11px] tabular-nums text-muted-foreground w-7 text-end">{tr.volume}</span>
                    </div>

                    {/* Per-track loop (disabled visual when sequence-loop is on) */}
                    <label className="flex items-center shrink-0" title={t('audioHub.loopTrack', 'Loop this track')}>
                      <Switch
                        checked={tr.loop}
                        disabled={pl.audio_loop_all}
                        onCheckedChange={v => updateTrack(tr.id, { loop: v })}
                      />
                    </label>

                    {/* Reorder + remove */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button" onClick={() => move(idx, 1)} disabled={idx === pl.tracks.length - 1}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button" onClick={() => removeTrack(tr.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* ── Right — audio preview (shared with the playlist page) ── */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <PreviewPanel mode="audio" items={audioItems} />
        </div>
      </div>

      {pickerOpen && (
        <AudioPickerOverlay
          excludeIds={pl.tracks.map(tr => tr.id)}
          onClose={() => setPickerOpen(false)}
          onAdd={addTracks}
        />
      )}

      <PublishDialog
        open={publishOpen}
        mode="publish"
        kind="audio"
        publish={(payload) => publishAudioPlaylist(pl.id, { screen_ids: payload.screen_ids, group_ids: payload.group_ids })}
        onClose={() => setPublishOpen(false)}
        onPublished={() => setPublishOpen(false)}
      />
    </div>
  )
}

// Lightweight audio picker — lists the org's live audio library (minus tracks
// already in the playlist) with multi-select. The media PlaylistContentPicker
// is image/video/playlist-only, so audio gets its own minimal overlay.
function AudioPickerOverlay({
  excludeIds, onClose, onAdd,
}: {
  excludeIds: number[]
  onClose: () => void
  onAdd: (files: AudioFile[]) => void
}) {
  const { t } = useTranslation()
  const [audios, setAudios] = useState<AudioFile[]>([])
  const [loading, setLoading] = useState(true)
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const exclude = new Set(excludeIds)

  useEffect(() => {
    getAudios()
      .then(r => setAudios(r.data.filter((a: AudioFile) => !exclude.has(a.id))))
      .catch(() => {})
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = (aid: number) => setPicked(prev => {
    const next = new Set(prev)
    if (next.has(aid)) next.delete(aid); else next.add(aid)
    return next
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-md p-5 space-y-3 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold">{t('audioHub.addTracks', 'Add tracks')}</h3>
        <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : audios.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">{t('audioHub.noMoreTracks', 'No more tracks to add.')}</p>
          ) : audios.map(a => {
            const checked = picked.has(a.id)
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggle(a.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-start transition-colors ${checked ? 'bg-muted' : 'hover:bg-muted/40'}`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
                  {checked && <Check className="w-3 h-3" />}
                </span>
                <Music className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                <span className="truncate">{a.original_name}</span>
              </button>
            )
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            size="sm"
            disabled={picked.size === 0}
            onClick={() => onAdd(audios.filter(a => picked.has(a.id)))}
          >
            {t('audioHub.addSelected', { defaultValue: 'Add {{count}}', count: picked.size })}
          </Button>
        </div>
      </div>
    </div>
  )
}
